import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserAuth } from "../../context/userAuthContext";
import {
  Settings,
  QrCode,
  CreditCard,
  Languages,
  TextCursor,
  Smartphone,
  Moon,
  Fingerprint,
  Shield,
  HelpCircle,
  ChevronRight,
  LogOut,
  UserCircle,
  Sparkles,
  Users,
  Mail,
  Phone,
  User,
} from "lucide-react";

const SettingsMenu = () => {
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user, logOut } = useUserAuth();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (user?.uid) {
          const docRef = window.db.collection("Users").doc(user.uid);
          const doc = await docRef.get();

          if (doc.exists) {
            const userData = doc.data();
            setUserInfo({
              name: `${userData.nombre} ${userData.apellido}`,
              nickname: userData.nickname,
              photoUrl: userData.photoUrl,
              email: userData.email,
              phone: userData.celular,
              edad: userData.edad,
              sexo: userData.sexo,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  const handleLogout = async () => {
    try {
      await logOut();
      navigate("/login");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const userDetailsItems = [
    {
      icon: <Mail className="w-6 h-6 text-orange-500" />,
      label: "Email",
      value: userInfo?.email,
    },
    {
      icon: <Phone className="w-6 h-6 text-orange-500" />,
      label: "Teléfono",
      value: userInfo?.phone,
    },
    {
      icon: <User className="w-6 h-6 text-orange-500" />,
      label: "Edad",
      value: userInfo?.edad ? `${userInfo.edad} años` : null,
    },
  ];

  const profileMenuItems = [
    {
      icon: <UserCircle className="w-6 h-6 text-orange-500" />,
      label: "Mis Actividades",
      description: "Gestiona tus actividades",
      path: "/myactivities",
    },
    {
      icon: <Sparkles className="w-6 h-6 text-orange-500" />,
      label: "Membresias",
      description: "Administra tus Membresias",
      path: "/memberships",
    },
    {
      icon: <Users className="w-6 h-6 text-orange-500" />,
      label: "Amigos",
      description: "Encuentra y gestiona tus conexiones",
      path: "/friends",
    },
  ];

  const settingsMenuItems = [
    {
      icon: <Settings className="w-6 h-6 text-orange-500" />,
      label: "Configuración",
      path: "/settings",
      rightIcon: <ChevronRight className="w-5 h-5 text-gray-400" />,
    },
    {
      icon: <QrCode className="w-6 h-6 text-orange-500" />,
      label: "Mi Código QR",
      path: "/qr-code",
    },
    {
      icon: <Languages className="w-6 h-6 text-orange-500" />,
      label: "Idioma",
      value: "🇪🇸",
    },
    {
      icon: <Moon className="w-6 h-6 text-orange-500" />,
      label: "Tema Oscuro",
      description: "Activar o desactivar modo oscuro",
      toggle: true,
    },
    {
      icon: <Shield className="w-6 h-6 text-orange-500" />,
      label: "PIN de 4 dígitos",
      description: "Activa la protección con PIN",
      toggle: true,
    },
    {
      icon: <HelpCircle className="w-6 h-6 text-orange-500" />,
      label: "Soporte y FAQ",
      description: "Encuentra soluciones y ayuda",
      path: "/support",
    },
  ];

  const MenuItem = ({ item }) => (
    <div
      className={`flex items-center justify-between bg-white p-4 rounded-xl shadow-sm cursor-pointer hover:shadow-md hover:scale-[1.01] transition-all duration-200 ${
        !item.path && !item.toggle ? "cursor-default hover:scale-100" : ""
      }`}
      onClick={() => item.path && navigate(item.path)}
    >
      <div className="flex items-center gap-4">
        <div className="p-2 bg-orange-50 rounded-xl">{item.icon}</div>
        <div>
          <div className="font-medium text-gray-800">{item.label}</div>
          {item.description && (
            <div className="text-sm text-gray-500">{item.description}</div>
          )}
        </div>
      </div>
      <div className="flex items-center">
        {item.value && (
          <span className="text-gray-600 text-sm">{item.value}</span>
        )}
        {item.toggle && (
          <div
            className={`w-12 h-6 rounded-full transition-colors duration-200 ${
              item.enabled ? "bg-orange-500" : "bg-gray-200"
            }`}
          >
            <div
              className={`w-5 h-5 bg-white rounded-full transform transition-transform duration-200 shadow-md ${
                item.enabled ? "translate-x-6" : "translate-x-1"
              } mt-0.5`}
            />
          </div>
        )}
        {item.rightIcon && item.rightIcon}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-orange-50 to-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      <div className="max-w-2xl mx-auto p-4">
        {/* Header con información del usuario */}
        <div className="bg-white p-6 rounded-2xl shadow-sm mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="relative group">
              <img
                src={userInfo?.photoUrl || "/api/placeholder/48/48"}
                alt="Profile"
                className="w-16 h-16 rounded-full object-cover border-2 border-orange-500 group-hover:border-orange-600 transition-all duration-200"
              />
              <div className="absolute -bottom-1 -right-1 bg-orange-500 rounded-full p-1 shadow-lg">
                <UserCircle className="w-4 h-4 text-white" />
              </div>
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-800">
                {userInfo?.name || "Usuario"}
              </h1>
              {userInfo?.nickname && (
                <p className="text-gray-500">@{userInfo.nickname}</p>
              )}
              <button
                onClick={() => navigate("/addprofile")}
                className="mt-2 px-4 py-1.5 bg-orange-500 text-white rounded-full text-sm font-medium hover:bg-orange-600 transition-colors duration-200"
              >
                Editar Perfil
              </button>
            </div>
            <button
              onClick={handleLogout}
              className="p-2.5 text-red-500 hover:bg-red-50 rounded-xl transition-colors duration-200 hover:shadow-md"
            >
              <LogOut className="w-6 h-6" />
            </button>
          </div>

          {/* Detalles del usuario */}
          <div className="space-y-3">
            {userDetailsItems.map(
              (item, index) =>
                item.value && <MenuItem key={`details-${index}`} item={item} />
            )}
          </div>
        </div>

        {/* Profile Menu Items */}
        <div className="space-y-3 mb-8">
          <h2 className="text-lg font-semibold text-gray-800 px-2 mb-4">
            Perfil
          </h2>
          {profileMenuItems.map((item, index) => (
            <MenuItem key={`profile-${index}`} item={item} />
          ))}
        </div>

        {/* Settings Menu Items */}
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-gray-800 px-2 mb-4">
            Configuración
          </h2>
          {settingsMenuItems.map((item, index) => (
            <MenuItem key={`settings-${index}`} item={item} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default SettingsMenu;
